import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../api";
import { useSettings } from "../../context/SettingsContext";

// ── Status milestone config ───────────────────────────────────────────────────
const MILESTONES = [
  {
    key: "pending",
    label: "Order Placed",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    key: "confirmed",
    label: "Confirmed",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: "processing",
    label: "Processing",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    key: "shipped",
    label: "Shipped",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
  },
  {
    key: "delivered",
    label: "Delivered",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
];

const MILESTONE_KEYS = MILESTONES.map((m) => m.key);

function StatusTracker({ status }) {
  const isCancelled = status === "cancelled" || status === "refunded";
  const activeIdx = isCancelled ? -1 : MILESTONE_KEYS.indexOf(status);

  if (isCancelled) {
    return (
      <div className="mb-6 flex items-center justify-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
        <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="text-red-700 font-semibold capitalize">{status === "refunded" ? "Order Refunded" : "Order Cancelled"}</p>
          <p className="text-red-500 text-xs">This order has been {status}. Contact support for assistance.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-5">Order Progress</h3>
      <div className="relative flex items-start justify-between">
        {/* Connector line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200" style={{ zIndex: 0 }}>
          <div
            className="h-full bg-emerald-500 transition-all duration-700 ease-in-out"
            style={{
              width: activeIdx < 0 ? "0%" : `${(activeIdx / (MILESTONES.length - 1)) * 100}%`,
            }}
          />
        </div>

        {MILESTONES.map((milestone, idx) => {
          const isDone = activeIdx >= idx;
          const isActive = activeIdx === idx;

          return (
            <div key={milestone.key} className="relative flex flex-col items-center flex-1" style={{ zIndex: 1 }}>
              {/* Circle */}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                  isDone
                    ? "bg-emerald-500 border-emerald-500 text-white shadow-md"
                    : "bg-white border-gray-300 text-gray-400"
                } ${isActive ? "ring-4 ring-emerald-100 scale-110" : ""}`}
              >
                {isDone && !isActive ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  milestone.icon
                )}
              </div>
              {/* Label */}
              <span
                className={`mt-2 text-center text-xs font-medium leading-tight ${
                  isDone ? "text-emerald-600" : "text-gray-400"
                } ${isActive ? "font-bold text-emerald-700" : ""}`}
              >
                {milestone.label}
              </span>
              {isActive && (
                <span className="mt-1 text-[10px] text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded-full font-semibold">
                  Current
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Order() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/orders/${id}`);
        setOrder(res?.data?.data || null);
      } catch (err) {
        setError(err?.response?.data?.message || "Order not found.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const { currencySymbol } = useSettings();
  const formatter = {
    format: (value) => `${currencySymbol} ${Number(value || 0).toFixed(2)}`,
  };

  const items = useMemo(() => order?.items || [], [order]);

  if (loading) {
    return (
      <div className="max-w-screen-2xl mx-auto py-16 px-3 sm:px-6 flex flex-col items-center justify-center gap-4">
        <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" />
        <p className="text-gray-500">Loading order details...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-screen-2xl mx-auto py-10 px-3 sm:px-6 text-red-500">
        {error || "Order not found."}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-screen-2xl mx-auto py-10 px-3 sm:px-6">
        {/* Thank you banner */}
        <div className="bg-emerald-100 rounded-xl mb-6 px-5 py-4 flex items-center gap-3">
          <svg className="w-6 h-6 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>
            Thank you{" "}
            <span className="font-bold text-emerald-700">{order.customer_name}</span>, your order has been received!
          </p>
        </div>

        {/* Status Tracker */}
        <StatusTracker status={order.status} />

        {/* Invoice */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between mb-6 border-b pb-4">
            <div>
              <h1 className="font-bold text-2xl uppercase">Invoice</h1>
              <p className="text-sm text-gray-500">Order #{order.order_number || order.id}</p>
            </div>
            <Link to="/" className="text-emerald-600 hover:underline text-sm font-medium">
              ← Back to Home
            </Link>
          </div>

          {/* Meta grid */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div>
              <div className="text-xs uppercase text-gray-400 font-semibold mb-0.5">Date</div>
              <div className="text-sm">{new Date(order.created_at).toLocaleDateString("en-BD", { year: "numeric", month: "long", day: "numeric" })}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-400 font-semibold mb-0.5">Phone</div>
              <div className="text-sm">{order.customer_phone || "N/A"}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-400 font-semibold mb-0.5">Shipping Address</div>
              <div className="text-sm">
                {typeof order.shipping_address === "object"
                  ? order.shipping_address?.address_line_1 || order.shipping_address?.street || "N/A"
                  : order.shipping_address || "N/A"}
              </div>
            </div>
          </div>

          {/* Items table */}
          <div className="overflow-x-auto border border-gray-100 rounded-lg">
            <table className="table-auto min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left text-xs font-semibold px-6 py-3 text-gray-500 uppercase">#</th>
                  <th className="text-left text-xs font-semibold px-6 py-3 text-gray-500 uppercase">Product</th>
                  <th className="text-center text-xs font-semibold px-6 py-3 text-gray-500 uppercase">Qty</th>
                  <th className="text-center text-xs font-semibold px-6 py-3 text-gray-500 uppercase">Unit Price</th>
                  <th className="text-right text-xs font-semibold px-6 py-3 text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {items.map((item, idx) => (
                  <tr key={item.id || idx} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-3 text-sm text-gray-600">{idx + 1}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">
                      {item.product_name}
                      {item.product_options?.label && (
                        <span className="text-xs text-emerald-600 block mt-0.5 font-medium">
                          Option: {item.product_options.label}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-center">{item.quantity}</td>
                    <td className="px-6 py-3 text-sm text-center">{formatter.format(item.unit_price || 0)}</td>
                    <td className="px-6 py-3 text-sm text-right font-semibold">{formatter.format(item.total_price || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals row */}
          <div className="mt-6 grid md:grid-cols-4 gap-4 bg-emerald-50 border border-emerald-100 p-5 rounded-lg">
            <div>
              <div className="text-xs uppercase text-gray-400 font-semibold mb-1">Payment</div>
              <div className="capitalize font-medium text-sm">{order.payment_method || "N/A"}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-400 font-semibold mb-1">Shipping</div>
              <div className="font-medium text-sm">{formatter.format(Number(order.shipping_amount || 0))}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-400 font-semibold mb-1">Discount</div>
              <div className="font-medium text-sm text-orange-500">{formatter.format(Number(order.discount_amount || 0))}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-gray-400 font-semibold mb-1">Total</div>
              <div className="text-xl font-extrabold text-red-500">{formatter.format(Number(order.total_amount || 0))}</div>
            </div>
          </div>

          {/* Tracking number if shipped */}
          {order.tracking_number && (
            <div className="mt-4 px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2 text-sm">
              <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-blue-700">
                Tracking number: <span className="font-mono font-semibold">{order.tracking_number}</span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Order;
